import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CatalogHardwareListItem } from '@kreart/shared';
import { Button } from '@/components/ui/button';
import { TextField, useFormContext } from '@/components/form';
import { positiveIntegerFieldProps } from '@/components/form/numberInputProps';
import {
  useCoefficientTarget,
  type CoefficientTarget,
} from '@/features/catalog/components/SaveCatalogCoefficientPrompt';
import { CoefficientField } from '@/features/project-items/shared/CoefficientField';
import { HardwareAutosuggest } from '@/features/project-items/shared/pickers/CatalogAutosuggest';

/** Shared type for hardware items in the field array. */
export interface HardwareItemValues {
  hardwareCatalogId: string;
  quantity: string;
  lengthM: string;
  coefficient: string;
  name?: string;
  sku?: string | null;
  manufacturer?: string | null;
  pricePerPiece?: string;
  currency?: string;
}

export function hardwareDisplayName(item: {
  name?: string;
  sku?: string | null;
  manufacturer?: string | null;
}): string {
  return [item.name, item.sku ? `SKU ${item.sku}` : null, item.manufacturer]
    .filter(Boolean)
    .join(' — ');
}

export function hardwarePriceLabel(item: {
  pricePerPiece?: string;
  currency?: string;
}): string | null {
  return item.pricePerPiece ? `${item.pricePerPiece} ${item.currency ?? ''}`.trim() : null;
}

export function dedupeHardwareItems<T extends { hardwareCatalogId: string; quantity: string }>(
  items: T[],
): T[] {
  const byCatalogId = new Map<string, T>();
  for (const item of items) {
    const existing = byCatalogId.get(item.hardwareCatalogId);
    if (!existing) {
      byCatalogId.set(item.hardwareCatalogId, item);
      continue;
    }
    const existingQuantity = parseInt(existing.quantity, 10);
    const nextQuantity = parseInt(item.quantity, 10);
    if (Number.isFinite(nextQuantity) && nextQuantity > (existingQuantity || 0)) {
      byCatalogId.set(item.hardwareCatalogId, item);
    }
  }
  return [...byCatalogId.values()];
}

/**
 * The field-array rows with row `index` pointed at another catalog item.
 *
 * The row's picker has already written the new id into its
 * `hardwareCatalogId`; what is left is the rest of the snapshot the row shows
 * and the price preview reads — name, SKU, manufacturer, unit price, currency —
 * which would otherwise still describe the item that was swapped out.
 *
 * Quantity, length and coefficient stay as they are: the swap answers "which
 * part", not "how much of it", and the numbers on screen are the ones the user
 * put there.
 *
 * Swapping onto an item another row already holds collapses the two. The list
 * is one row per catalog item — the add buttons move an existing row to the top
 * instead of adding a second, and `dedupeHardwareItems` drops the rest on the
 * way out — so leaving two identical rows on screen would only mean one of them
 * silently vanishing at Save. The edited row is the one that survives, with the
 * quantity the user is looking at.
 *
 * Returns `null` for an index that is not there, which the caller drops.
 */
export function hardwareItemsWithReplacement<T extends HardwareItemValues>(
  items: readonly T[],
  index: number,
  picked: CatalogHardwareListItem,
): T[] | null {
  if (!items[index]) return null;
  return items
    .map((row, rowIndex) =>
      rowIndex === index
        ? {
            ...row,
            hardwareCatalogId: picked.id,
            name: picked.name,
            sku: picked.sku,
            manufacturer: picked.manufacturer,
            pricePerPiece: picked.pricePerPiece,
            currency: picked.currency,
          }
        : row,
    )
    .filter((row, rowIndex) => rowIndex === index || row.hardwareCatalogId !== picked.id);
}

/**
 * A single hardware item row inside a cabinet form's field array.
 * Shows: name | qty | length(m) | coeff | edit | delete
 */
export function HardwareItemRow({
  index,
  hardware,
  onRemove,
  onReplace,
  askCoefficient,
}: {
  index: number;
  hardware: HardwareItemValues;
  onRemove: () => void;
  /**
   * Swap this row onto another catalog item — see
   * `hardwareItemsWithReplacement`, which the caller applies to its field
   * array.
   */
  onReplace: (item: CatalogHardwareListItem) => void;
  /**
   * From `useCatalogCoefficientPrompt`. Given, leaving the markup offers to
   * make it this fitting's catalog default; omitted, the field is a plain
   * number box.
   */
  askCoefficient?: (target: CoefficientTarget, coefficient: string) => void;
}) {
  const { t } = useTranslation();
  const form = useFormContext();
  // Resolved for every row, picked or hydrated, so a cabinet opened for editing
  // can be asked about too. Cached per catalog id, so the same fitting on two
  // rows costs one read.
  const coefficientTarget = useCoefficientTarget(
    'hardware',
    hardware.hardwareCatalogId,
    t('projectItems.cabinets.fields.hardwareCatalogId'),
    { enabled: Boolean(askCoefficient) },
  );
  // A picked row reads as a caption, so it renders as one — until Edit turns it
  // back into the picker it was chosen with.
  const [editing, setEditing] = useState(false);
  // What the row pointed at when the edit started, so an abandoned one can put
  // it back. See `stopEditing`.
  const catalogIdBeforeEdit = useRef('');
  const priceLabel = hardwarePriceLabel(hardware);
  const catalogIdName = `hardwareItems.${index}.hardwareCatalogId`;

  const startEditing = () => {
    catalogIdBeforeEdit.current = String(form.getValues(catalogIdName) ?? '');
    setEditing(true);
  };

  /**
   * Leave edit mode, refusing to leave the row pointing at nothing.
   *
   * Typing in the picker clears the id it is bound to until a suggestion is
   * chosen, and the row goes back to being a <p> plus a hidden input the moment
   * this runs — so an abandoned edit would pin `selectedHardwareSchema`'s
   * required-id error on a control the user cannot see or fix, and Save would
   * refuse with nothing on screen to explain it. Restoring the previous item is
   * the only outcome that leaves the form usable; deleting the row is the way
   * to be rid of it.
   */
  const stopEditing = () => {
    if (!String(form.getValues(catalogIdName) ?? '')) {
      form.setValue(catalogIdName, catalogIdBeforeEdit.current, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
    setEditing(false);
  };

  return (
    <div className="grid gap-1.5 rounded-md border border-border bg-card p-1.5 md:grid-cols-[minmax(0,1fr)_6rem_5rem_6rem_auto_auto]">
      {editing ? (
        <HardwareAutosuggest
          name={catalogIdName}
          label={t('projectItems.cabinets.fields.hardwareCatalogId')}
          placeholder={t('pricing.pickHardware')}
          // The row gives this picker a fraction of the dialog's width, and a
          // hardware label is a name, a manufacturer and a SKU — the same reason
          // the cut list's material picker asks for a roomier list.
          roomyList
          onSelectedOption={(item) => {
            // Null is the picker reporting a half-typed search, not a choice —
            // the row stays open until a real suggestion is taken.
            if (!item) return;
            onReplace(item);
            setEditing(false);
          }}
        />
      ) : (
        <div className="min-w-0 self-center">
          <input type="hidden" {...form.register(catalogIdName)} />
          <p className="truncate text-sm font-medium text-foreground">
            {hardwareDisplayName(hardware)}
          </p>
          {priceLabel && <p className="text-xs text-muted-foreground">{priceLabel}</p>}
        </div>
      )}
      <TextField
        name={`hardwareItems.${index}.quantity`}
        label={t('projectItems.cabinets.fields.quantity')}
        {...positiveIntegerFieldProps}
        className="h-7 text-sm"
      />
      <TextField
        name={`hardwareItems.${index}.lengthM`}
        label={t('projectItems.hardware.fields.lengthM')}
        type="number"
        inputMode="decimal"
        min={0}
        step={0.1}
        className="h-7 text-sm"
      />
      {askCoefficient ? (
        <CoefficientField
          name={`hardwareItems.${index}.coefficient`}
          label={t('projectItems.cabinets.fields.coefficient')}
          target={coefficientTarget}
          ask={askCoefficient}
          className="h-7 text-sm"
        />
      ) : (
        <TextField
          name={`hardwareItems.${index}.coefficient`}
          label={t('projectItems.cabinets.fields.coefficient')}
          type="number"
          inputMode="decimal"
          min={0.01}
          step={0.1}
          className="h-7 text-sm"
        />
      )}
      {/* Ahead of Delete, so the destructive one stays the last thing in the
          row and a mis-click lands on the recoverable action. */}
      <Button
        type="button"
        variant="ghost"
        className="h-7 self-end px-2 text-xs"
        onClick={editing ? stopEditing : startEditing}
        title={t('projectItems.cabinets.hardware.replace')}
      >
        {editing ? t('common.cancel') : t('common.edit')}
      </Button>
      <Button
        type="button"
        variant="ghost"
        className="h-7 self-end px-2 text-xs"
        onClick={onRemove}
      >
        {t('common.delete')}
      </Button>
    </div>
  );
}
